import { supabase } from './supabase';

// Module-level language state, initialized from localStorage so it's correct
// on first page load before I18nProvider's async useEffect fires.
let currentLang: string = (() => {
  try { return localStorage.getItem('professya_lang') ?? 'pt-BR'; }
  catch { return 'pt-BR'; }
})();
export function setCurrentLang(lang: string): void {
  currentLang = lang;
}

// Edge function calls go through Vercel's reverse proxy (/api/sb/:fn) to avoid
// CORS preflight failures. The proxy rewrites to Supabase but the browser sees
// a same-origin request, so no OPTIONS preflight is sent.
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function edgeFunctionUrl(functionName: string): string {
  return `/api/sb/${functionName}`;
}

interface SafeInvokeOptions<T> {
  body?: Record<string, unknown>;
  /** Fallback value returned on error or timeout */
  fallback: T;
  /** Timeout in ms (default 28 000) */
  timeout?: number;
}

export interface StreamInvokeOptions {
  body?: Record<string, unknown>;
  /** Timeout in ms (default 90 000, streaming needs more room) */
  timeout?: number;
  /** Called each time a new chunk arrives; `accumulated` is the full text so far */
  onChunk: (accumulated: string) => void;
  /** Called when streaming completes; `fullText` is the complete response */
  onComplete: (fullText: string) => void;
  /** Called on network error, timeout, or server error */
  onError: (err: string) => void;
}

interface SafeInvokeResult<T> {
  data: T;
  /** True when the fallback was returned instead of real data */
  fromFallback: boolean;
  error?: string;
}

// All our Edge Functions wrap responses in { ok: boolean, data: T, error?: string }.
function unwrap<T>(raw: unknown, fallback: T): { data: T; fromFallback: boolean; error?: string } {
  if (raw && typeof raw === 'object' && 'ok' in raw) {
    const envelope = raw as { ok: boolean; data?: unknown; error?: string };
    if (envelope.ok) {
      return { data: (envelope.data ?? fallback) as T, fromFallback: false };
    }
    return {
      data: (envelope.data ?? fallback) as T,
      fromFallback: true,
      error: envelope.error ?? 'Function returned ok: false',
    };
  }
  return { data: raw as T, fromFallback: false };
}

// ─── JWT decode helpers ───────────────────────────────────────────────────────
function b64urlDecode(part: string): unknown {
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
      + '='.repeat((4 - part.length % 4) % 4);
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

// Returns the `exp` claim from a JWT payload, or null.
function jwtExp(token: string): number | null {
  const payload = b64urlDecode(token.split('.')[1]) as Record<string, unknown> | null;
  return typeof payload?.exp === 'number' ? payload.exp : null;
}

// Returns a short diagnostic string: "alg=ES256 exp=ok" or similar.
function jwtDiag(token: string): string {
  const hdr = b64urlDecode(token.split('.')[0]) as Record<string, unknown> | null;
  const pay = b64urlDecode(token.split('.')[1]) as Record<string, unknown> | null;
  const alg = hdr?.alg ?? '?';
  const exp = pay?.exp;
  const expStr = typeof exp === 'number'
    ? (exp > Date.now() / 1000 ? `exp_ok(${Math.round(exp - Date.now() / 1000)}s)` : 'EXPIRED')
    : 'no_exp';
  return `alg=${alg} ${expStr}`;
}

// True if the token won't expire in the next 10 seconds.
function isFresh(token: string): boolean {
  const exp = jwtExp(token);
  return exp !== null && exp > Date.now() / 1000 + 10;
}

// ─── Auth failure broadcast ───────────────────────────────────────────────────
// Fired when a function returns 401 even after token refresh.
// Any component can subscribe via window.addEventListener('auth:session-expired', …)
function broadcastSessionExpired(): void {
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
}


// Maintained by onAuthStateChange so we always hold the token the SDK manages.

let _cachedToken: string | null = null;

supabase.auth.getSession().then(({ data: { session } }) => {
  _cachedToken = session?.access_token ?? null;
});

supabase.auth.onAuthStateChange((_event, session) => {
  _cachedToken = session?.access_token ?? null;
});

/**
 * Return the current user's JWT.
 *
 * Fast path: return the cached token if it has more than 10 seconds left.
 * Slow path: call getSession(), Supabase JS v2.45+ auto-refreshes expired
 * tokens inside getSession() using the stored refresh token.
 */
async function resolveJwt(): Promise<string | null> {
  if (_cachedToken && isFresh(_cachedToken)) return _cachedToken;

  // getSession() auto-refreshes if the access token is already past expiry
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;

  _cachedToken = session.access_token;
  return _cachedToken;
}

/** Low-level fetch to a single edge function with the given JWT. */
function edgeFetch(
  functionName: string,
  jwt: string,
  bodyPayload: Record<string, unknown>,
  signal: AbortSignal,
): Promise<Response> {
  return fetch(
    edgeFunctionUrl(functionName),
    {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey':        SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${jwt}`,
      },
      body:   JSON.stringify(bodyPayload),
      signal,
    },
  );
}

/**
 * Safely invoke a Supabase Edge Function with timeout and automatic fallback.
 * Never throws, always returns a value.
 *
 * On 401 the function forces a full token refresh via refreshSession().
 * This handles all invalidity causes:
 *   • Expired token (common)
 *   • JWT secret rotation (makes existing tokens invalid regardless of exp)
 *   • Clock skew beyond the 10 s isFresh buffer
 *
 * refreshSession() in Supabase JS v2 acquires a distributed lock, so calling
 * it alongside the SDK's own auto-refresh is safe, they serialize, not race.
 */
/**
 * Invoke a Supabase Edge Function that returns an SSE stream.
 * Calls onChunk as each accumulated text arrives, onComplete when done.
 * Never throws, always calls onError on failure.
 */
export async function streamInvoke(
  functionName: string,
  { body = {}, timeout = 90_000, onChunk, onComplete, onError }: StreamInvokeOptions,
): Promise<void> {
  const jwt = await resolveJwt();
  if (!jwt) { onError('No auth session'); return; }

  const controller  = new AbortController();
  const timer       = setTimeout(() => controller.abort(), timeout);
  const bodyPayload = { language: currentLang, ...body };

  try {
    const response = await fetch(
      edgeFunctionUrl(functionName),
      {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey':        SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${jwt}`,
        },
        body:   JSON.stringify(bodyPayload),
        signal: controller.signal,
      },
    );

    clearTimeout(timer);

    if (!response.ok || !response.body) {
      onError(`HTTP ${response.status}`);
      return;
    }

    const reader  = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer    = '';
    let accumulated = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      // SSE events are delimited by \n\n
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const dataLine = part.split('\n').find(l => l.startsWith('data: '));
        if (!dataLine) continue;
        try {
          const event = JSON.parse(dataLine.slice(6)) as Record<string, unknown>;
          if (typeof event.error === 'string') {
            onError(event.error);
            return;
          }
          if (typeof event.chunk === 'string') {
            accumulated = event.chunk; // chunk already contains accumulated text
            onChunk(accumulated);
          }
          if (event.done === true) {
            const full = typeof event.text === 'string' ? event.text : accumulated;
            onComplete(full);
            return;
          }
        } catch { /* skip malformed events */ }
      }
    }

    onComplete(accumulated);
  } catch (err) {
    clearTimeout(timer);
    const isTimeout = err instanceof Error && err.name === 'AbortError';
    console.error(`[streamInvoke] ${functionName} ${isTimeout ? 'timeout' : 'error'}:`, err);
    onError(isTimeout ? 'timeout' : String(err));
  }
}

export async function safeInvoke<T>(
  functionName: string,
  { body = {}, fallback, timeout = 28_000 }: SafeInvokeOptions<T>,
): Promise<SafeInvokeResult<T>> {
  const jwt = await resolveJwt();

  if (!jwt) {
    return { data: fallback, fromFallback: true, error: 'No auth session' };
  }

  const controller  = new AbortController();
  const timer       = setTimeout(() => controller.abort(), timeout);
  const bodyPayload = { language: currentLang, ...body };

  try {
    console.debug(`[safeInvoke] ${functionName} attempt1 token: ${jwtDiag(jwt)}`);
    let response = await edgeFetch(functionName, jwt, bodyPayload, controller.signal);

    // ── 401 retry ────────────────────────────────────────────────────────────
    // The token can be invalid for reasons beyond simple expiry (e.g. JWT secret
    // rotation, clock skew).  Force a refresh to get a newly signed token.
    if (response.status === 401) {
      // Wait briefly: if the SDK's own auto-refresh just fired, _cachedToken
      // will already be updated and we can skip the extra network call.
      await new Promise(r => setTimeout(r, 300));

      let retryJwt: string | null = null;

      if (_cachedToken && _cachedToken !== jwt) {
        // SDK refreshed during our wait, use the new token directly.
        retryJwt = _cachedToken;
        console.debug(`[safeInvoke] ${functionName} retry using SDK-refreshed token: ${jwtDiag(retryJwt)}`);
      } else {
        // SDK hasn't refreshed yet, call refreshSession() ourselves.
        const { data: rd, error: refreshErr } = await supabase.auth.refreshSession();
        if (rd.session) {
          retryJwt = rd.session.access_token;
          _cachedToken = retryJwt;
          console.debug(`[safeInvoke] ${functionName} retry after refreshSession: ${jwtDiag(retryJwt)}`);
        } else {
          console.warn(`[safeInvoke] ${functionName} refreshSession failed:`, refreshErr?.message ?? 'no session returned');
        }
      }

      if (retryJwt) {
        response = await edgeFetch(functionName, retryJwt, bodyPayload, controller.signal);
      }
    }

    clearTimeout(timer);

    if (!response.ok) {
      const errText = await response.text().catch(() => String(response.status));
      console.error(`[safeInvoke] ${functionName} HTTP ${response.status}:`, errText);
      // If both the original call AND the retry returned 401, the session is
      // genuinely invalid (key rotation, expired refresh token, etc.).
      // Broadcast so the UI can prompt the user to sign in again.
      if (response.status === 401) broadcastSessionExpired();
      return { data: fallback, fromFallback: true, error: `HTTP ${response.status}: ${errText}` };
    }

    const raw = await response.json().catch(() => null);
    return unwrap<T>(raw, fallback);
  } catch (err) {
    clearTimeout(timer);
    const isTimeout = err instanceof Error && err.name === 'AbortError';
    console.error(`[safeInvoke] ${functionName} ${isTimeout ? 'timeout' : 'exception'}:`, err);
    return {
      data: fallback,
      fromFallback: true,
      error: isTimeout ? 'timeout' : String(err),
    };
  }
}
